import { useCallback, useEffect, useRef, useState } from "react";

export interface IntersectionObserverData {
  isIntersecting: boolean;
  intersectionRatio: number;
  boundingClientRect: DOMRectReadOnly;
  intersectionRect: DOMRectReadOnly;
  rootBounds: DOMRectReadOnly | null;
  target: Element;
  time: number;
}

export interface UseIntersectionObserverOptions {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  triggerOnce?: boolean;
  triggerOnLeaveViewport?: boolean;
}

export interface UseIntersectionObserverReturn {
  ref: (node: Element | null) => void;
  isIntersecting: boolean;
  entry: IntersectionObserverEntry | null;
}

export const useIntersectionObserver = (
  callback?: (data: IntersectionObserverData) => void,
  options: UseIntersectionObserverOptions = {}
): UseIntersectionObserverReturn => {
  const {
    root = null,
    rootMargin = "0px",
    threshold = 0,
    triggerOnce = false,
    triggerOnLeaveViewport = false,
  } = options;

  const [isIntersecting, setIsIntersecting] = useState(false);
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const [hasTriggered, setHasTriggered] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementRef = useRef<Element | null>(null);
  const previousIntersectingRef = useRef<boolean>(false);

  const ref = useCallback(
    (node: Element | null) => {
      // Disconnect previous observer if it exists
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      // Store the element reference
      elementRef.current = node;

      if (!node) {
        setIsIntersecting(false);
        setEntry(null);
        previousIntersectingRef.current = false;
        return;
      }

      // Skip if triggerOnce is true and already triggered
      if (triggerOnce && hasTriggered) {
        return;
      }

      // Create new observer
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [observerEntry] = entries;
          const isCurrentlyIntersecting = observerEntry.isIntersecting;

          setIsIntersecting(isCurrentlyIntersecting);
          setEntry(observerEntry);

          // Call the callback with formatted data
          if (callback) {
            const previousIntersecting = previousIntersectingRef.current;

            let shouldTriggerCallback = false;

            if (triggerOnLeaveViewport) {
              // Trigger only when transitioning from not intersecting to intersecting
              shouldTriggerCallback =
                !previousIntersecting && isCurrentlyIntersecting;
            } else {
              // Normal behavior - trigger on any intersection change
              shouldTriggerCallback = true;
            }

            // Update previous intersecting state BEFORE calling callback to prevent multiple calls
            previousIntersectingRef.current = isCurrentlyIntersecting;

            if (shouldTriggerCallback) {
              const data: IntersectionObserverData = {
                isIntersecting: isCurrentlyIntersecting,
                intersectionRatio: observerEntry.intersectionRatio,
                boundingClientRect: observerEntry.boundingClientRect,
                intersectionRect: observerEntry.intersectionRect,
                rootBounds: observerEntry.rootBounds,
                target: observerEntry.target,
                time: observerEntry.time,
              };
              callback(data);
            }
          } else {
            // Update previous intersecting state even if no callback
            previousIntersectingRef.current = isCurrentlyIntersecting;
          }

          // Mark as triggered if triggerOnce is enabled and element is intersecting
          if (triggerOnce && isCurrentlyIntersecting) {
            setHasTriggered(true);
          }
        },
        {
          root,
          rootMargin,
          threshold,
        }
      );

      // Start observing
      observerRef.current.observe(node);
    },
    [
      callback,
      root,
      rootMargin,
      threshold,
      triggerOnce,
      triggerOnLeaveViewport,
      hasTriggered,
    ]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return {
    ref,
    isIntersecting,
    entry,
  };
};

export default useIntersectionObserver;
